import { Component, Prop, Ref, SimpleEvent } from '@eastsideco/salvo-ts';
import { ThemeComponent } from "@/theme/ThemeComponent";
import axios from 'axios';
import { parseUrl, stringifyUrl } from 'query-string';

@Component
export class LoadMore extends ThemeComponent {
    @Prop currentPage!: number;
    @Prop previous!: boolean;
    @Prop itemContainerSelector!: string;
    @Prop itemSelector!: string;

    @Ref({ required: false }) button!: HTMLButtonElement;

    private _onLoading = new SimpleEvent<boolean>();
    public get onLoading() {
        return this._onLoading.asEvent();
    }

    init() {

        // Listen for button click
        this.listenForButtonClick();
    }

    private listenForButtonClick() {
        if (this.button) {
            this.$listen(this.button, 'click', (e) => {
                e.preventDefault();
                void this.loadMore();
            });
        }
    }

    private async loadMore() {

        // Set loading
        this._onLoading.dispatch(true);

        // Determine next page URL
        const { url, query } = parseUrl(window.location.href, { decode: false });
        let nextPageNumber = this.currentPage + 1;
        if (this.previous) {
            nextPageNumber = this.currentPage - 1;
        }
        const nextPageUrl = stringifyUrl({
            url: url,
            query: {
                ...query,
                page: nextPageNumber,
            }
        }, { arrayFormat: 'none', encode: false });

        // Make request for the next page
        const response = await axios.get(nextPageUrl);

        // Update browser showing URL
        window.history.replaceState({}, document.title, nextPageUrl);

        // Get new elements from response data and add them to a new elements wrapper
        const newElementsWrapper = document.createElement('div');
        newElementsWrapper.innerHTML = response.data;

        // Update pagination elements
        let newResults: HTMLElement|null = null;
        if (this.previous) {
            newResults = newElementsWrapper.querySelector('.load-more__results--previous');
        } else {
            newResults = newElementsWrapper.querySelector('.load-more__results--next');
        }
        let loadMoreButtonExists: boolean = false;
        if (this.previous) {
            loadMoreButtonExists = newElementsWrapper.querySelector('.load-more__button--previous') !== null;
        } else {
            loadMoreButtonExists = newElementsWrapper.querySelector('.load-more__button--next') !== null;
        }
        this.updatePaginationElements(newResults, loadMoreButtonExists);

        // Add new elements to page
        const newElements = newElementsWrapper.querySelectorAll(this.itemSelector);
        this.addNewElements(newElements);

        // Increase current page
        this.currentPage = nextPageNumber;

        // Set loading
        this._onLoading.dispatch(false);
    }

    updatePaginationElements(newResults: HTMLElement|null, loadMoreButtonExists: boolean) {

        // Results
        let oldResults: HTMLElement|null = null;
        if (this.previous) {
            oldResults = this.querySelector('.load-more__results--previous');
        } else {
            oldResults = this.querySelector('.load-more__results--next');
        }
        if (newResults !== null && oldResults !== null && oldResults.parentNode !== null) {
            oldResults.parentNode.replaceChild(newResults, oldResults);
        }

        // If there's a new button showing, we know there are more results to show
        // If there isn't - there's no results left and we can remove the button
        if (!loadMoreButtonExists) {
            if (this.button) {
                this.button.remove();
            }
        }
    }

    addNewElements(elements: NodeListOf<Element>) {

        // Determine order of elements
        const elementsArray = [...elements];
        if (this.previous) {
            elementsArray.reverse();
        }

        // Loop through each new element
        for (const element of elementsArray) {

            // Append new element to the DOM
            const itemContainer = document.querySelector(this.itemContainerSelector);
            if (itemContainer) {
                if (this.previous) {
                    itemContainer.prepend(element);
                } else {
                    itemContainer.appendChild(element);
                }
            }
        }
    }
}
